/**
 * Logger Utility for Pair App
 *
 * Features:
 * - Log levels: debug, info, warn, error
 * - Production (env=prod): Only warn/error output (unless debug mode or test account)
 * - Staging (env=pre): All levels output always
 * - Development (env=dev): All levels output always
 * - Debug mode: ?debug=true URL param enables all levels in prod
 * - Structured prefix: [PAIR-APP] [LEVEL]
 * - PostHog integration for errors
 */

import posthog from 'posthog-js';
import getConfig from 'next/config';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
    prefix: string;
    enableDebugMode: boolean;
}

const DEFAULT_CONFIG: LoggerConfig = {
    prefix: '[PAIR-APP]',
    enableDebugMode: true,
};

/**
 * Check if we're in a browser environment
 */
const isBrowser = typeof window !== 'undefined';

/**
 * Check if we're in a production build (NODE_ENV)
 * Note: This is true for both staging and production builds
 */
const isProductionBuild = process.env.NODE_ENV === 'production';

/**
 * Get the actual environment from config ('dev', 'pre', or 'prod')
 */
function getEnvironment(): string {
    try {
        const { publicRuntimeConfig } = getConfig() || {};
        return publicRuntimeConfig?.niioConfig?.env || 'dev';
    } catch {
        // getConfig() can fail during SSR initial load
        return 'dev';
    }
}

/**
 * Check if we're in ACTUAL production (env=prod, not dev or pre/staging)
 * - env=prod: production (filter logs)
 * - env=pre: staging (show all logs)
 * - env=dev: development (show all logs)
 */
function isActualProduction(): boolean {
    return getEnvironment() === 'prod';
}

// Legacy export for backward compatibility
const isProduction = isProductionBuild;

/**
 * Check if debug mode is enabled via URL parameter or sessionStorage
 */
function isDebugModeEnabled(): boolean {
    if (!isBrowser) return false;

    // Check sessionStorage first (persists during session)
    const storedDebug = sessionStorage.getItem('pair_debug_mode');
    if (storedDebug === 'true') return true;

    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');

    if (debugParam === 'true') {
        // Store in sessionStorage so it persists during navigation
        sessionStorage.setItem('pair_debug_mode', 'true');
        return true;
    }

    return false;
}

/**
 * Get current debug mode status (cached for performance)
 */
let cachedDebugMode: boolean | null = null;
function getDebugMode(): boolean {
    if (cachedDebugMode === null) {
        cachedDebugMode = isDebugModeEnabled();
    }
    return cachedDebugMode;
}

/**
 * Reset debug mode cache (call when URL changes)
 */
export function resetDebugModeCache(): void {
    cachedDebugMode = null;
}

/**
 * Check if debug mode is currently active
 */
export function isDebugMode(): boolean {
    return getDebugMode();
}

/**
 * Enable debug mode programmatically
 */
export function enableDebugMode(): void {
    if (isBrowser) {
        sessionStorage.setItem('pair_debug_mode', 'true');
        cachedDebugMode = true;
    }
}

/**
 * Disable debug mode programmatically
 */
export function disableDebugMode(): void {
    if (isBrowser) {
        sessionStorage.removeItem('pair_debug_mode');
        cachedDebugMode = false;
    }
}

/**
 * Enable debug mode for test accounts (auto-enabled in actual production only)
 * This allows test accounts to see all debug logs without needing ?debug=true
 * Note: In staging (env=pre), all logs are shown anyway, so this is only needed in production
 */
export function enableDebugModeForTestAccount(isTestAccount: boolean): void {
    if (!isBrowser) return;

    if (isTestAccount && isActualProduction()) {
        sessionStorage.setItem('pair_debug_mode', 'true');
        sessionStorage.setItem('pair_debug_mode_reason', 'test_account');
        cachedDebugMode = true;
        // Log immediately so testers know debug mode is active
        console.log(
            '[PAIR-APP] [INFO] Debug mode auto-enabled for test account'
        );
    }
}

/**
 * Check if debug mode was enabled due to test account
 */
export function isDebugModeFromTestAccount(): boolean {
    if (!isBrowser) return false;
    return sessionStorage.getItem('pair_debug_mode_reason') === 'test_account';
}

/**
 * Check if a log level should be output
 */
function shouldLog(level: LogLevel): boolean {
    // In development or staging, always log everything
    if (!isActualProduction()) return true;

    // In production with debug mode, log everything
    if (getDebugMode()) return true;

    // In production without debug mode, only log warn and error
    return level === 'warn' || level === 'error';
}

/**
 * Format log arguments with prefix
 */
function formatArgs(
    level: LogLevel,
    prefix: string,
    args: unknown[]
): unknown[] {
    const timestamp = new Date().toISOString();
    const levelTag = `[${level.toUpperCase()}]`;

    // In debug mode, include timestamp
    if (getDebugMode()) {
        return [`${prefix} ${levelTag} [${timestamp}]`, ...args];
    }

    return [`${prefix} ${levelTag}`, ...args];
}

/**
 * Send error to PostHog for tracking
 */
function sendErrorToPostHog(
    message: string,
    error?: Error,
    context?: Record<string, unknown>
): void {
    if (!isBrowser) return;

    try {
        // Use posthog.capture with $exception event type
        posthog.capture('$exception', {
            $exception_message: message,
            $exception_type: error?.name || 'Error',
            $exception_stack: error?.stack,
            $exception_source: 'logger',
            $current_url: window.location.href,
            ...context,
        });
    } catch (captureError) {
        // Silently fail - don't log errors about logging errors
    }
}

/**
 * Create a logger instance
 */
function createLogger(config: LoggerConfig = DEFAULT_CONFIG) {
    const { prefix } = config;

    return {
        /**
         * Debug level - only in development or debug mode
         * Use for verbose debugging information
         */
        debug(...args: unknown[]): void {
            if (shouldLog('debug')) {
                console.log(...formatArgs('debug', prefix, args));
            }
        },

        /**
         * Info level - only in development or debug mode
         * Use for general information
         */
        info(...args: unknown[]): void {
            if (shouldLog('info')) {
                console.info(...formatArgs('info', prefix, args));
            }
        },

        /**
         * Warn level - always logged
         * Use for warnings that don't break functionality
         */
        warn(...args: unknown[]): void {
            if (shouldLog('warn')) {
                console.warn(...formatArgs('warn', prefix, args));
            }
        },

        /**
         * Error level - always logged + sent to PostHog
         * Use for errors that affect functionality
         */
        error(
            message: string,
            error?: Error,
            context?: Record<string, unknown>
        ): void {
            if (shouldLog('error')) {
                if (error) {
                    console.error(
                        ...formatArgs('error', prefix, [message, error])
                    );
                } else {
                    console.error(...formatArgs('error', prefix, [message]));
                }
            }

            // Always send errors to PostHog in production builds (staging + prod)
            if (isProductionBuild) {
                sendErrorToPostHog(message, error, context);
            }
        },

        /**
         * Group related logs together
         */
        group(label: string): void {
            if (!isActualProduction() || getDebugMode()) {
                console.group(`${prefix} ${label}`);
            }
        },

        /**
         * End a log group
         */
        groupEnd(): void {
            if (!isActualProduction() || getDebugMode()) {
                console.groupEnd();
            }
        },

        /**
         * Log with a specific context/component name
         */
        withContext(context: string) {
            const contextPrefix = `${prefix} [${context}]`;
            return createLogger({ ...config, prefix: contextPrefix });
        },
    };
}

// Export default logger instance
const logger = createLogger();

export default logger;

// Export for testing and external use
export {
    createLogger,
    shouldLog,
    isProduction,
    isProductionBuild,
    isActualProduction,
    getEnvironment,
    isBrowser,
};
